'use client';
import dynamic from 'next/dynamic';
import withLoadingProps from 'next-dynamic-loading-props';
import { useId, useState, cloneElement, useEffect } from 'react';
import { components } from 'react-select';
import { CaretDownOutline, CloseOutline } from '@raresail/react-ionicons';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from '@/contexts/language-context';

const ReactSelect = withLoadingProps((loadingProps) =>
  dynamic(() => import('react-select'), {
    ssr: false,
    loading: () => {
      const { className } = loadingProps();
      return (
        <select
          className={twMerge(
            'select input-beautifi shadow-beautifi',
            className
          )}
        ></select>
      );
    }
  })
);

export const Control = ({ children, ...props }) => {
  const initialIcon = props.selectProps?.icon;
  const icon = initialIcon
    ? cloneElement(initialIcon, {
        className: twMerge('shrink-0', initialIcon.props?.className)
      })
    : null;

  return (
    <components.Control {...props}>
      <div className="w-full flex items-center">
        {icon}
        {children}
      </div>
    </components.Control>
  );
};

export const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <CaretDownOutline className="w-2.5 h-2.5" />
    </components.DropdownIndicator>
  );
};

export const MultiValueLabel = ({ children, ...props }) => {
  return (
    <components.MultiValueLabel {...props}>
      {props?.data?.multiLabel || children}
    </components.MultiValueLabel>
  );
};

export const MultiValueRemove = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <CloseOutline className="icon-sm" />
    </components.MultiValueRemove>
  );
};

export const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <CloseOutline className="w-5 h-5 mr-2 opacity-80" />
    </components.ClearIndicator>
  );
};

export const MenuList = ({ children, selectProps, ...props }) => {
  return (
    <components.MenuList {...props}>
      {selectProps?.listHeader}
      {children}
      {selectProps?.listFooter}
    </components.MenuList>
  );
};

const findOption = (value, options) => {
  if (Array.isArray(value) && value.every((e) => e.value && e.label)) {
    return value;
  } else if (value?.label && value?.value) {
    return value;
  }

  let hasMany = false;
  let vals = [];
  if (Array.isArray(value)) {
    hasMany = true;
    vals = value;
  } else if (value?.includes?.('|')) {
    hasMany = true;
    vals = value.split('|');
  } else {
    vals = [value];
  }

  const results = vals.map((value) => {
    let match = options.find((o) => o.value === value);
    if (!match) {
      options.find((option) => {
        return (
          option.options &&
          (match = option.options.find((o) => o.value === value))
        );
      });
    }
    return match;
  });
  return hasMany ? results : results[0];
};

export function Select({
  label,
  id: propId,
  options = [],
  containerClassName,
  labelClassName,
  className,
  onChange,
  isMulti,
  value,
  error,
  errorClassName,
  defaultValue,
  menuPortalTarget,
  noOptionsMessage,
  placeholder,
  instruction,
  instructionClassName,
  searchBox,
  searchBoxPlaceholder,
  searchBoxIcon,
  ...props
}) {
  const { t } = useTranslation('beautifi');
  const [selectedValue, setSelectedValue] = useState(
    findOption(value || defaultValue || '', options)
  );

  const id = useId();
  const doOnChange = (selectedVal) => {
    setSelectedValue(selectedVal);
    if (onChange && Array.isArray(selectedVal)) {
      onChange(selectedVal.map((s) => s.value).join('|'));
    } else if (onChange) {
      onChange(selectedVal?.value || null);
    }
  };

  useEffect(() => {
    if (value !== undefined && value !== selectedValue?.value) {
      const foundOpts = findOption(value || '', options);
      if (foundOpts) {
        setSelectedValue(foundOpts);
      } else {
        setSelectedValue(null);
      }
    }
  }, [selectedValue, value, options]);

  let portalTarget = menuPortalTarget;
  try {
    portalTarget =
      menuPortalTarget || (typeof document !== 'undefined' && document.body);
  } catch (err) {
    /* ignore */
  }

  return (
    <fieldset className={twMerge('fieldset w-full', containerClassName)}>
      {!!label && (
        <legend className="contents">
          <label
            className={twMerge('fieldset-legend pb-1', labelClassName)}
            htmlFor={propId || id}
          >
            {label}
          </label>
        </legend>
      )}
      <ReactSelect
        {...props}
        unstyled
        instanceId={id}
        inputId={propId || id}
        value={selectedValue}
        onChange={doOnChange}
        isMulti={isMulti}
        noOptionsMessage={() =>
          noOptionsMessage || t('select.noOptions', 'No Options')
        }
        loadingMessage={() =>
          noOptionsMessage || t('select.loading', 'Loading')
        }
        placeholder={placeholder || t('select.placeholder', 'Select...')}
        menuPortalTarget={portalTarget}
        isSearchable={!searchBox}
        components={{
          Control,
          DropdownIndicator,
          MultiValueLabel,
          MultiValueRemove,
          MenuList,
          ClearIndicator
        }}
        className={twMerge(
          'rselect',
          isMulti && 'rselect-multi',
          error ? 'input-error text-error' : '',
          className
        )}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 20 })
        }}
        classNames={{
          control: () => 'rselect-control',
          menu: () => 'rselect-menu',
          menuPortal: () => 'z-100!',
          menuList: () => 'rselect-menu-list',
          option: ({ isSelected, isFocused }) =>
            twMerge(
              'rselect-option',
              isSelected && 'rselect-option-selected',
              isFocused && 'rselect-option-focused'
            ),
          noOptionsMessage: () => 'rselect-no-options-message',
          valueContainer: () => 'rselect-value-container',
          multiValueLabel: () => 'rselect-multi-value-label',
          multiValueRemove: () => 'rselect-multi-value-remove',
          placeholder: () => 'rselect-placeholder',
          groupHeading: () => 'rselect-group-heading'
        }}
        options={options}
      />
      {!!error?.message && (
        <p className={twMerge('fieldset-label text-error', errorClassName)}>
          {error?.message}
        </p>
      )}
      {!!instruction && (
        <div className={twMerge('fieldset-label', instructionClassName)}>
          {typeof instruction === 'string' ? (
            <span className="opacity-60">{instruction}</span>
          ) : (
            instruction
          )}
        </div>
      )}
    </fieldset>
  );
}
